import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import BaseAuthenticator from 'ember-simple-auth/authenticators/base';
import ENV from 'pix-orga/config/environment';
import { decodeToken } from 'pix-orga/utils/jwt';

export default class OidcAuthenticator extends BaseAuthenticator {
  static id = 'oidc';

  @service featureToggles;
  @service oidcIdentityProviders;
  @service session;
  @service requestManager;

  async authenticate({ code, state, iss, identityProviderSlug, authenticationKey, hostSlug }) {
    const request = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
    const host = `${ENV.APP.API_HOST}/api/oidc/`;
    let body;
    const identityProvider = this.oidcIdentityProviders.findBySlug(identityProviderSlug);

    if (authenticationKey) {
      body = {
        identity_provider: identityProvider.code,
        authentication_key: authenticationKey,
      };
    } else {
      body = {
        identity_provider: identityProvider.code,
        code,
        state,
        iss,
      };

      if (this.session.isAuthenticated) {
        this.session.set('skipRedirectAfterSessionInvalidation', true);
        await this.session.invalidate();
      }
    }

    request.body = JSON.stringify({ data: { attributes: body } });
    const response = await fetch(host + hostSlug, request);

    const data = await response.json();
    if (!response.ok) {
      return Promise.reject(data);
    }

    const decodedAccessToken = decodeToken(data.access_token);

    return {
      access_token: data.access_token,
      user_id: decodedAccessToken.user_id,
      expiresAt: decodedAccessToken.exp * 1000,
      logoutUrlUuid: data.logout_url_uuid,
      source: identityProvider.source,
      shouldCloseSession: identityProvider.shouldCloseSession,
      identityProviderCode: identityProvider.code,
    };
  }

  restore(data) {
    return new Promise((resolve, reject) => {
      if (isEmpty(data['access_token'])) {
        reject();
      }
      if (data.expiresAt <= new Date().getTime()) {
        reject();
      }

      resolve(data);
    });
  }

  /**
   * @param {Object} data - The current authenticated session data
   */
  async invalidate(data) {
    const { access_token, shouldCloseSession, identityProviderCode, logoutUrlUuid } = data || {};

    if (this.featureToggles.featureToggles.isSessionLogoutEnabled) {
      try {
        const response = await this.requestManager.request({
          url: `${ENV.APP.API_HOST}/api/oidc/logout`,
          method: 'POST',
          body: JSON.stringify({
            identity_provider: identityProviderCode,
            logout_url_uuid: logoutUrlUuid,
          }),
        });

        if (this.session.routeAfterInvalidation) {
          return;
        }

        const { redirectLogoutUrl } = response.content;
        if (redirectLogoutUrl) {
          this.session.routeAfterInvalidation = redirectLogoutUrl;
        }
      } catch (err) {
        // eslint-disable-next-line no-console -- for diagnostics
        console.log('Pix API logout failed but ignoring error to clear authentication on client side:', err);
      }
    } else {
      // Old implementation

      if (!shouldCloseSession || this.session.routeAfterInvalidation) {
        return;
      }

      const response = await fetch(
        `${ENV.APP.API_HOST}/api/oidc/redirect-logout-url?identity_provider=${identityProviderCode}&logout_url_uuid=${logoutUrlUuid}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );
      const { redirectLogoutUrl } = await response.json();

      this.session.routeAfterInvalidation = redirectLogoutUrl;
    }
  }
}
